import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  CONNECTION_STATUS,
  chronicleDeviceStateAtom,
  chronicleIsInputAtom,
  chronicleSessionIdAtom,
} from '@/atoms/chronicle';
import { store } from '@/store';

/**
 * Auto-selects Chronicle as the active input source when the device connects,
 * and reverts to the system default when the device leaves the connected
 * state (while idle).
 *
 * Runs at the provider level so the side nav's VoiceIndicator renders
 * ChronicleBars (not DesktopBars) even before the user visits a session page.
 */
export function useChronicleAutoSelectInput() {
  const deviceState = useAtomValue(chronicleDeviceStateAtom);
  const setIsChronicleInput = useSetAtom(chronicleIsInputAtom);
  const sessionId = useAtomValue(chronicleSessionIdAtom);

  const prevStatusRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = deviceState.connectionStatus;
    prevStatusRef.current = curr;

    // Fresh connect → assert Chronicle as the active input, UNLESS a desktop-origin
    // recording is already live (APP-7654). A desktop-mic session keeps
    // chronicleSessionId === null, so a running timer with no Chronicle session means
    // a local recording is in progress; flipping the input to Remote would re-point
    // its Stop button at the Chronicle stop path (a no-op) and strand it. A Chronicle
    // recording (sessionId set) is unaffected; idle connects still auto-select Remote.
    if (curr === CONNECTION_STATUS.CONNECTED && prev !== CONNECTION_STATUS.CONNECTED) {
      const desktopRecordingInProgress =
        store.getState().timerModel.isRunning && sessionId === null;
      if (!desktopRecordingInProgress) {
        setIsChronicleInput(true);
      }
      return;
    }

    // Whenever the device is NOT connected and we're idle, fall back to the
    // system default. This covers every non-connected status (RECONNECTING,
    // ERROR, SCANNING, CONNECTING, DISCONNECTED) — important because Rust
    // triggers auto-reconnect immediately on BLE transport end, so the FE
    // typically transits CONNECTED → RECONNECTING (not directly to
    // DISCONNECTED). Also re-fires when sessionId clears post-interrupt
    // while still disconnected, which the previous transition-only logic
    // missed.
    if (curr !== CONNECTION_STATUS.CONNECTED && sessionId === null) {
      setIsChronicleInput(false);
    }
  }, [deviceState.connectionStatus, sessionId, setIsChronicleInput]);
}
